import type {KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent} from 'react';
import {useCallback, useEffect, useLayoutEffect, useRef} from 'react';

import type {CalculatorWindowPosition} from './calculatorWindow.store';

/** Distance one arrow-key press moves the window. */
const NUDGE_PX = 16;

/** Offset custom properties consumed by the window's `translate-x/y-[…]` classes. */
export const WINDOW_X_VAR = '--calculator-window-x';
export const WINDOW_Y_VAR = '--calculator-window-y';

interface DragBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface UseCalculatorDragOptions {
  /** Committed offset; the starting point for the next gesture. */
  position: CalculatorWindowPosition;
  /** Called once per gesture on release, and when a viewport change pulls the window back in. */
  onCommit: (position: CalculatorWindowPosition) => void;
  /** The element being moved. Measured to clamp, and written to during a drag. */
  windowRef: React.RefObject<HTMLElement | null>;
  /** Dragging is floating-mode only — docked and expanded windows don't move. */
  enabled: boolean;
  /** Height of fixed chrome above the page that the window may not slide under, in px. */
  topInset?: number;
}

/** Spread straight onto the drag handle element. */
interface UseCalculatorDragReturn {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
}

/**
 * When the window is bigger than the viewport the bounds cross, and each axis keeps the
 * edge that holds the controls: the right, for close, and the top, for the title bar.
 * The window's max height keeps the vertical bounds from crossing in practice.
 */
const clampTo = ({x, y}: CalculatorWindowPosition, bounds: DragBounds) => ({
  x: Math.min(Math.max(x, bounds.minX), bounds.maxX),
  y: Math.max(Math.min(y, bounds.maxY), bounds.minY)
});

/**
 * Pointer-drag plus arrow-key nudge for the calculator window's title bar.
 *
 * A gesture moves the window by writing its offset custom properties straight
 * to the node, and only commits to the store on release. Routing every
 * `pointermove` through React state instead would re-render the calculator
 * bodies — a canvas renderer and a MathLive editor — on every frame of a drag.
 *
 * Bounds are measured once, at pointer-down: the window's own size can't change
 * mid-gesture, so re-measuring per frame would only risk reading a box that
 * hasn't caught up with the offset already written.
 */
export function useCalculatorDrag({
  position,
  onCommit,
  windowRef,
  enabled,
  topInset = 0
}: UseCalculatorDragOptions): UseCalculatorDragReturn {
  const gestureRef = useRef<{
    pointerX: number;
    pointerY: number;
    origin: CalculatorWindowPosition;
    bounds: DragBounds;
  } | null>(null);
  const liveRef = useRef<CalculatorWindowPosition>(position);

  useEffect(() => {
    liveRef.current = position;
  }, [position]);

  /** Offsets that keep the whole window inside the viewport. */
  const measureBounds = useCallback((): DragBounds | null => {
    const rect = windowRef.current?.getBoundingClientRect();
    // A box that isn't laid out measures 0x0; clamping against it drags the
    // window to the top-left corner instead of keeping it on screen.
    if (!rect || rect.width === 0) {
      return null;
    }
    // The rect already includes the live offset, so back it out to get the
    // untranslated box and express the limits relative to that.
    const baseLeft = rect.left - liveRef.current.x;
    const baseTop = rect.top - liveRef.current.y;

    return {
      minX: -baseLeft,
      maxX: window.innerWidth - baseLeft - rect.width,
      minY: topInset - baseTop,
      maxY: window.innerHeight - baseTop - rect.height
    };
  }, [windowRef, topInset]);

  const moveTo = useCallback(
    (next: CalculatorWindowPosition) => {
      liveRef.current = next;
      const element = windowRef.current;
      element?.style.setProperty(WINDOW_X_VAR, `${next.x}px`);
      element?.style.setProperty(WINDOW_Y_VAR, `${next.y}px`);
    },
    [windowRef]
  );

  const endGesture = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!gestureRef.current) {
        return;
      }
      gestureRef.current = null;
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      // Bounds were measured at pointer-down; the viewport may have shrunk since.
      const bounds = measureBounds();
      if (bounds) {
        moveTo(clampTo(liveRef.current, bounds));
      }
      onCommit(liveRef.current);
    },
    [measureBounds, moveTo, onCommit]
  );

  // A mode change detaches these handlers mid-gesture (only a floating window
  // drags), so pointerup never reaches endGesture and leaves a stale origin.
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- cancels an in-flight pointer gesture the parent cannot observe */
  useEffect(() => {
    if (!enabled) {
      gestureRef.current = null;
    }
  }, [enabled]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */

  useLayoutEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const clampToViewport = () => {
      if (gestureRef.current) {
        return;
      }
      const bounds = measureBounds();
      if (!bounds) {
        return;
      }
      const next = clampTo(liveRef.current, bounds);
      if (next.x !== liveRef.current.x || next.y !== liveRef.current.y) {
        moveTo(next);
        onCommit(next);
      }
    };
    // Also whenever it becomes draggable again: the viewport may have changed
    // while it was closed or expanded, with nothing listening.
    clampToViewport();
    window.addEventListener('resize', clampToViewport);
    return () => window.removeEventListener('resize', clampToViewport);
  }, [enabled, measureBounds, moveTo, onCommit]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled || event.button !== 0) {
        return;
      }
      const bounds = measureBounds();
      if (!bounds) {
        return;
      }
      event.preventDefault();
      gestureRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        origin: liveRef.current,
        bounds
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [enabled, measureBounds]
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const gesture = gestureRef.current;
      if (!gesture || !enabled) {
        return;
      }
      moveTo(
        clampTo(
          {
            x: gesture.origin.x + event.clientX - gesture.pointerX,
            y: gesture.origin.y + event.clientY - gesture.pointerY
          },
          gesture.bounds
        )
      );
    },
    [enabled, moveTo]
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (!enabled) {
        return;
      }
      const delta = {
        ArrowLeft: {x: -NUDGE_PX, y: 0},
        ArrowRight: {x: NUDGE_PX, y: 0},
        ArrowUp: {x: 0, y: -NUDGE_PX},
        ArrowDown: {x: 0, y: NUDGE_PX}
      }[event.key];

      const bounds = delta && measureBounds();
      if (!delta || !bounds) {
        return;
      }
      event.preventDefault();
      const next = clampTo(
        {x: liveRef.current.x + delta.x, y: liveRef.current.y + delta.y},
        bounds
      );
      moveTo(next);
      onCommit(next);
    },
    [enabled, measureBounds, moveTo, onCommit]
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: endGesture,
    onPointerCancel: endGesture,
    onKeyDown
  };
}
